// financeEngine.js
// 🔥 FONTE ÚNICA DE VERDADE para todos os cálculos financeiros do Phoenix OS.
// Usado por: MobileInterface.jsx, DashboardInterface.jsx, GestorDashboardInterface.jsx
// Nunca duplique essas fórmulas em outro arquivo — sempre importe daqui.

/**
 * 🔥 NOVO: rótulos e dicas dos campos financeiros, sensíveis ao segmento.
 * Resolve o caso da Holding/Grupo — decisão fechada com o Samuel: em vez de
 * criar um formulário inteiro separado pra esse segmento (mais complexidade
 * de manutenção), reaproveitamos os MESMOS três campos (receita, custos,
 * dívida) só trocando o RÓTULO e adicionando uma dica de contexto quando o
 * segmento é Holding — porque numa holding esses três campos significam
 * algo diferente:
 *   - "Faturamento" não existe no sentido tradicional — é dividendo, aluguel
 *     ou JCP recebido das controladas.
 *   - "Custos" são administrativos/financeiros da holding, não o custo
 *     operacional das empresas controladas (que têm seu próprio diagnóstico).
 *   - "Dívida" muitas vezes está garantida pelas controladas — risco de
 *     cross-default que vale mencionar.
 * O motor de cálculo (calculateCoreMetrics, calculateHealthStatus) não muda
 * nada — os números entram exatamente como em qualquer outro segmento, só
 * a PERGUNTA feita pra pessoa é mais precisa pro caso dela.
 */
export function getFinancialFieldLabels(sector) {
    if (sector === 'Holding / Grupo') {
        return {
            revenueLabel: 'Receita Operacional (Mensal)',
            revenueHint: 'Dividendos, aluguéis e JCP recebidos das controladas — não é "faturamento" no sentido tradicional.',
            costsLabel: 'Custos Administrativos e Financeiros (Mensal)',
            costsHint: 'Despesas de gestão, contábeis e financeiras da própria holding — não inclui o custo operacional das controladas.',
            debtHint: 'Inclua também dívidas das controladas garantidas pela holding — risco de "cross-default" (uma controlada em default pode acionar a dívida da holding inteira).',
        };
    }
    return {
        revenueLabel: 'Faturamento Bruto (Mensal)',
        revenueHint: null,
        costsLabel: 'Custos e Despesas Totais (Mensal)',
        costsHint: null,
        debtHint: null,
    };
}

/**
 * Múltiplo de valuation por setor (EBITDA x múltiplo x 12 = Enterprise Value)
 */
export function getSectorMultiple(sector) {
    const multiples = {
        'Varejo': 4.5,
        'Alimentação': 5.0,
        'Tech/SaaS': 8.5,
        'Indústria': 6.0,
        'Serviços': 4.5,
        'Saúde/Clínicas': 7.0,
        'Logística/Frota': 5.0,
        'Holding / Grupo': 6.5,
        'Atacado/Distrib.': 5.0,
        'Agronegócio': 6.5,
        'Construção/Eng.': 4.0,
    };
    return multiples[sector] || 5.5;
}

/**
 * Ciclo financeiro médio de mercado, em dias, por setor (referência aproximada
 * de mercado brasileiro — usada só como contexto comparativo, não é dado oficial).
 * Ciclo financeiro = dias que a empresa demora a receber - dias que demora a pagar.
 * Quanto MAIOR, mais capital de giro a operação exige.
 */
export function getSectorCycleBenchmark(sector) {
    const ciclos = {
        'Varejo': 18,
        'Alimentação': 22,
        'Tech/SaaS': 10,
        'Indústria': 45,
        'Serviços': 20,
        'Saúde/Clínicas': 35,
        'Logística/Frota': 30,
        'Holding / Grupo': 30,
        'Atacado/Distrib.': 40,
        'Agronegócio': 60,
        'Construção/Eng.': 50,
    };
    return ciclos[sector] !== undefined ? ciclos[sector] : 30;
}

/**
 * Métricas centrais — EBITDA, alavancagem, margem, valuation, risco de ruptura.
 * Usado em todo lugar que precisa desses números (app, dashboard gestor, dashboard consultor).
 *
 * 🔥 UNIDADE (decisão de 2026-10-01): receita e custos são MENSAIS (o que o
 * pequeno dono realmente sabe de cabeça). Tudo que é "por ano" nesta
 * função é derivado de x12: valuation (EBITDA x múltiplo x 12), alavancagem
 * (dívida / EBITDA anualizado — o padrão de mercado, que é o que os limiares
 * de getSectorLeverageThresholds esperam) e cobertura de juros. A dívida é
 * um saldo (não tem período), e a taxa de juros é % ao ano.
 *
 * @param {number} rev - Receita MENSAL (número, não string formatada)
 * @param {number} cos - Custos MENSAIS (número, não string formatada)
 * @param {number} deb - Dívida total (número, não string formatada)
 * @param {string} sector - Setor de atuação (para o múltiplo de valuation)
 * @param {object} [advanced] - Dados avançados opcionais (retrocompatível: se omitido, comportamento é idêntico ao anterior)
 * @param {number} [advanced.custoNaoRecorrente] - Custo pontual (não recorrente) a ser "devolvido" ao EBITDA
 * @param {number} [advanced.custoDivida] - Taxa de juros média da dívida, em % ao ano
 */
export function calculateCoreMetrics(rev, cos, deb, sector, advanced = {}) {
    const ebitda = rev - cos;

    // 🔥 EBITDA AJUSTADO: prática padrão de M&A — custos pontuais (uma reforma, um
    // processo judicial, etc.) não representam a operação real, então são "devolvidos"
    // ao EBITDA para refletir a capacidade real de geração de caixa da empresa.
    const custoNaoRecorrente = Number(advanced.custoNaoRecorrente) || 0;
    const ebitdaAjustado = ebitda + custoNaoRecorrente;

    // 🔥 CUSTO DO SERVIÇO DA DÍVIDA: quanto a empresa paga de juros por ano.
    const custoDividaPct = Number(advanced.custoDivida) || 0;
    const custoServicoDivida = deb > 0 ? deb * (custoDividaPct / 100) : 0;
    // Juros são anuais; o EBITDA é mensal — compara com o EBITDA anualizado.
    const coberturaJuros = custoServicoDivida > 0
        ? Number(((ebitdaAjustado * 12) / custoServicoDivida).toFixed(2))
        : null;

    // Alavancagem e valuation usam o EBITDA Ajustado quando disponível (mais realista);
    // sem custoNaoRecorrente, ebitdaAjustado === ebitda, então nada muda pra quem não preencheu.
    // 🔥 CORRIGIDO (2026-10-01): dividia a dívida pelo EBITDA de UM MÊS, o que
    // inflava a alavancagem ~12x e jogava empresas saudáveis em Atenção/Crítico.
    // Alavancagem de mercado = dívida / EBITDA dos últimos 12 meses.
    const alavancagem = deb > 0 && ebitdaAjustado > 0
        ? Number((deb / (ebitdaAjustado * 12)).toFixed(2))
        : (deb > 0 && ebitdaAjustado <= 0 ? "10.00+" : 0);

    const currentMargin = rev > 0
        ? Number(((ebitda / rev) * 100).toFixed(1))
        : 0;

    const multiple = getSectorMultiple(sector);
    const baseEbitda = ebitdaAjustado > 0 ? ebitdaAjustado : 0;
    const enterpriseValue = baseEbitda * multiple * 12;
    const equityValue = Math.max(0, enterpriseValue - deb);

    const alavancagemNum = alavancagem === "10.00+" ? 10 : Number(alavancagem);
    let ruptureRisk = Math.min(100, Math.max(0, (alavancagemNum * 15) - (currentMargin * 2)));
    if (ebitdaAjustado <= 0) ruptureRisk = 99;
    // 🔥 CORREÇÃO: contas com números decimais em JavaScript podem gerar
    // resultados tipo "40.449999999999996" em vez de "40.45" — é uma
    // limitação conhecida da linguagem, não um erro de fórmula.
    // Arredondamos aqui, na fonte, pra sair limpo em toda tela que usa
    // esse número.
    ruptureRisk = Math.round(ruptureRisk * 10) / 10;

    return {
        ebitda, ebitdaAjustado, custoServicoDivida, coberturaJuros,
        alavancagem, currentMargin, multiple, enterpriseValue, equityValue, ruptureRisk
    };
}

/**
 * 🔥 NOVO: limiares de alavancagem (Dívida/EBITDA) por segmento, usados por
 * calculateHealthStatus abaixo. Antes, TODO segmento usava o mesmo limiar
 * fixo (>2.5x Atenção, >=4.5x Crítico, >7x Falência) — isso classificava
 * errado setores estruturalmente mais (ou menos) alavancados: uma
 * Construtora com 6x de alavancagem é normal pro setor, mas o limiar
 * genérico já marcava isso como "Crítico".
 *
 * ⚠️ PROPOSTA INICIAL baseada em práticas comuns de análise de crédito/M&A
 * (múltiplos de Dívida Líquida/EBITDA por setor) — NÃO é calibração final.
 * O Samuel tem formação em turnaround/reestruturação e deve revisar estes
 * números antes de considerar isso "fechado"; ajuste os valores abaixo
 * livremente, a lógica de `calculateHealthStatus` não muda.
 *
 * `atencao`/`critico`/`falencia` = alavancagem ACIMA da qual o status vira
 * aquele nível (limiar é sempre "maior que", nunca "maior ou igual", pra
 * ficar simples de calibrar: o valor informado é o teto do nível anterior).
 */
export function getSectorLeverageThresholds(sector) {
    const limiares = {
        // Asset-light / ciclo curto — tolerância menor a dívida.
        'Varejo': { atencao: 2.5, critico: 4, falencia: 6 },
        'Alimentação': { atencao: 2.5, critico: 4, falencia: 6 },
        'Serviços': { atencao: 2.5, critico: 4, falencia: 6 },
        'Tech/SaaS': { atencao: 2, critico: 3.5, falencia: 5 },
        // Capital intensivo — tolerância maior (ativos financiáveis: máquinas, frota, imóveis).
        'Indústria': { atencao: 3, critico: 5, falencia: 8 },
        'Logística/Frota': { atencao: 3, critico: 5, falencia: 7 },
        'Atacado/Distrib.': { atencao: 3, critico: 5, falencia: 7 },
        'Saúde/Clínicas': { atencao: 2.5, critico: 4, falencia: 6 },
        // Ciclos sazonais/plurianuais — dívida mais alta é normal no meio do ciclo.
        'Agronegócio': { atencao: 3.5, critico: 6, falencia: 9 },
        // Setor com maior tolerância do mercado a alavancagem (financiamento de obra).
        'Construção/Eng.': { atencao: 5, critico: 10, falencia: 14 },
        // ⚠️ Holding: "alavancagem" aqui é uma aproximação imperfeita — o
        // problema real é estrutural (receita da holding não é
        // "faturamento" tradicional, é dividendo/aluguel/JCP das
        // controladas, e a dívida pode estar garantida por elas). Este
        // limiar é só um paliativo até o app tratar Holding com campos de
        // entrada próprios (ver observação no CLAUDE.md / conversa com o Samuel).
        'Holding / Grupo': { atencao: 3, critico: 5, falencia: 8 },
    };
    // Fallback = os limiares genéricos originais, pra qualquer segmento sem
    // entrada específica na tabela (nunca deixa sem classificação).
    return limiares[sector] || { atencao: 2.5, critico: 4.5, falencia: 7 };
}

/**
 * Classificação de status de saúde financeira (rótulo + cor), derivada das
 * métricas centrais. Usado em qualquer lugar que precise mostrar um badge
 * de "Saudável / Atenção / Crítico / Falência Iminente".
 *
 * `sector` é opcional: sem ele, usa o limiar genérico (comportamento
 * idêntico ao de antes desta mudança) — permite migrar os pontos de
 * chamada aos poucos sem quebrar nada que ainda não passa o segmento.
 */
export function calculateHealthStatus({ ebitda, alavancagem, deb, rev, sector }) {
    const alavancagemNum = alavancagem === "10.00+" ? 10 : Number(alavancagem);
    const { atencao, critico, falencia } = getSectorLeverageThresholds(sector);

    // Dívida acima de 50% do faturamento ANUAL (a receita é mensal, daí x12).
    if ((ebitda < 0 && deb > (rev * 12 * 0.5)) || alavancagemNum > falencia) {
        return { label: 'Falência Iminente', colorKey: 'red600' };
    }
    if (alavancagemNum >= critico || ebitda <= 0) {
        return { label: 'Crítico', colorKey: 'red500' };
    }
    if (alavancagemNum > atencao) {
        return { label: 'Atenção', colorKey: 'orange500' };
    }
    return { label: 'Saudável', colorKey: 'lime400' };
}

/**
 * Métricas estendidas — só fazem sentido no relatório completo do Gestor
 * (não usadas nos dashboards, que mostram só a visão consolidada da carteira).
 * NCG = Necessidade de Capital de Giro.
 *   - Se prazoRecebimento e prazoPagamento forem informados, calcula o ciclo
 *     financeiro real: (receita diária = receita mensal / 30) x (dias recebendo - dias pagando).
 *   - Sem esses dados, mantém a aproximação simples de 20% da receita (retrocompatível).
 * Reserva de emergência = 6 meses de custo operacional
 * Dividendos seguros = % do EBITDA que pode ser distribuído sem comprometer a saúde
 * Score M&A = pontuação de 10-100 usada como "nota" rápida de atratividade
 * Cobertura de curto prazo = quantas vezes o EBITDA cobre a dívida que vence este ano
 */
// Aceita número OU texto no formato brasileiro ("65.000,00"). O app passava o texto do campo, e Number("65.000,00")
// dá NaN -> 0: pró-labore e dívida de curto prazo sumiam dos cálculos (caixa mínimo igual aos custos).
function numeroBR(v) {
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    if (v === null || v === undefined || v === '') return 0;
    const t = String(v).trim();
    const n = t.includes(',') ? Number(t.split('.').join('').replace(',', '.')) : Number(t);
    return Number.isFinite(n) ? n : 0;
}

// Versão da metodologia dos números enviados à IA. Quando muda (ex.: a projeção passou a partir do EBITDA ajustado),
// todo parecer guardado com versão antiga é refeito UMA vez ao abrir o dossiê. Só incremente quando os números mudarem de fato.
export const METODO_PARECER = 'ebitda-ajustado-v8-status';

export function calculateExtendedMetrics({ rev, cos, ebitda, alavancagem, currentMargin, prazoRecebimento, prazoPagamento, dividaCurtoPrazo, proLabore, sector }) {
    const alavancagemNum = alavancagem === "10.00+" ? 10 : Number(alavancagem);
    const baseEbitda = ebitda > 0 ? ebitda : 0;

    const temPrazos = prazoRecebimento !== undefined && prazoRecebimento !== '' && prazoPagamento !== undefined && prazoPagamento !== '';
    let ncg;
    if (temPrazos) {
        const cicloFinanceiro = Number(prazoRecebimento) - Number(prazoPagamento);
        // A receita é MENSAL: receita por dia = rev / 30 (antes dividia por 365,
        // como se fosse anual, e subestimava a necessidade de capital de giro ~12x).
        ncg = (rev / 30) * cicloFinanceiro; // pode ser negativo (fornecedor financiando a operação — bom sinal)
    } else {
        ncg = rev * 0.20;
    }

    const emergencyReserve = cos * 6;

    let payoutRatio = 0.40;
    if (alavancagemNum > 2.5) payoutRatio = 0.15;
    if (alavancagemNum > 4.5 || ebitda <= 0) payoutRatio = 0.00;
    const safeDividends = Math.max(0, baseEbitda * payoutRatio * 12);

    const dividaCP = numeroBR(dividaCurtoPrazo);
    // A dívida de curto prazo vence ao longo do ano; compara com o EBITDA anualizado.
    const coberturaCurtoPrazo = dividaCP > 0 ? Number(((baseEbitda * 12) / dividaCP).toFixed(2)) : null;

    // 🎯 SCORE DE PRONTIDÃO PARA M&A (10–100) — 3 pilares, com a alavancagem medida contra o limiar DO SETOR
    // (antes era uma régua única, e uma construtora de 16% de margem chegava ao teto de 100 sem esforço):
    //   Alavancagem vs setor (-20 a +25) + Margem (-25 a +20) + Cobertura da dívida de curto prazo (-10 a +8), base 40.
    //   100 só com os três pilares excelentes; sem dívida de curto prazo informada, o 3º pilar fica neutro.
    const lim = getSectorLeverageThresholds(sector);
    let score = 40;
    if (!(ebitda > 0)) score -= 20;
    else if (alavancagemNum <= lim.atencao * 0.5) score += 25;
    else if (alavancagemNum <= lim.atencao) score += 15;
    else if (alavancagemNum <= lim.critico) score += 0;
    else score -= 20;
    if (currentMargin > 20) score += 20;
    else if (currentMargin > 10) score += 12;
    else if (currentMargin > 5) score += 4;
    else if (currentMargin >= 0) score -= 10;
    else score -= 25;
    if (coberturaCurtoPrazo !== null) {
        if (coberturaCurtoPrazo >= 3) score += 8;
        else if (coberturaCurtoPrazo >= 1.5) score += 4;
        else if (coberturaCurtoPrazo < 1) score -= 10;
    }
    const maScore = Math.min(100, Math.max(10, Math.round(score)));

    // 🔥 CAIXA MÍNIMO — o pró-labore era coletado no cadastro da empresa mas
    // nunca entrava em nenhum cálculo. Faz sentido como o "chão" de caixa que
    // a operação precisa ter todo mês: os custos fixos + o que os sócios
    // retiram pra viver, já que essa retirada normalmente não para mesmo em
    // mês ruim. É diferente da Reserva de Emergência (que é 6 meses desse
    // valor, pra imprevistos) — Caixa Mínimo é o piso mensal recorrente.
    const proLaboreNum = numeroBR(proLabore);
    const caixaMinimo = cos + proLaboreNum;

    return { ncg, emergencyReserve, safeDividends, maScore, coberturaCurtoPrazo, caixaMinimo, proLaboreNum };
}

/**
 * 🔮 PROJEÇÃO ESTOCÁSTICA — motor da funcionalidade de previsão de risco.
 * Projeta EBITDA e status de saúde financeira pra frente no tempo, em 3
 * cenários (Pessimista / Provável / Otimista). A faixa entre os cenários
 * aumenta quanto mais longe no tempo, porque incerteza se acumula.
 *
 * Regra de crescimento da tendência: usa a margem atual como "pulso" do
 * negócio — se a margem é boa, a tendência-base é levemente positiva;
 * se é ruim, é levemente negativa. Isso é INTENCIONALMENTE simples (não é
 * IA/estatística avançada) — é o esqueleto numérico. A narrativa em texto
 * (explicação do que os números significam) é gerada pela IA depois,
 * usando esses números como base factual.
 *
 * @param {object} metrics - saída de calculateCoreMetrics (ebitda, currentMargin, alavancagem)
 * @param {number} rev - receita mensal atual
 * @returns {Array<{months:number, pessimista:number, provavel:number, otimista:number}>}
 */
export function calculateStochasticProjection(metrics, rev) {
    // 🔥 Base = EBITDA AJUSTADO (a mesma base de valuation, alavancagem, saque e coberturas). Antes partia do
    // EBITDA sem ajuste, e a projeção ficava ~10% abaixo do restante do relatório.
    const { currentMargin } = metrics;
    const ebitda = (metrics.ebitdaAjustado !== undefined && metrics.ebitdaAjustado !== null) ? metrics.ebitdaAjustado : metrics.ebitda;

    // Tendência-base mensal: margem boa puxa levemente pra cima, margem
    // ruim puxa levemente pra baixo. Limitado a +/-1.5% ao mês pra não
    // gerar projeções absurdas em prazos longos.
    let tendenciaMensal = 0;
    if (currentMargin > 15) tendenciaMensal = 0.010;
    else if (currentMargin > 5) tendenciaMensal = 0.004;
    else if (currentMargin >= 0) tendenciaMensal = -0.004;
    else tendenciaMensal = -0.012;

    // Faixa de incerteza por horizonte — cresce com o tempo (ver doc acima).
    const faixaPorHorizonte = { 3: 0.05, 6: 0.08, 12: 0.15, 24: 0.25, 36: 0.35 };
    const horizontes = [3, 6, 12, 24, 36];

    return horizontes.map(months => {
        const base = ebitda * Math.pow(1 + tendenciaMensal, months);
        const faixa = faixaPorHorizonte[months];
        return {
            months,
            pessimista: Number((base * (1 - faixa)).toFixed(2)),
            provavel: Number(base.toFixed(2)),
            otimista: Number((base * (1 + faixa)).toFixed(2)),
        };
    });
}

/**
 * Projeção de cenário simulado (usado nos sliders de "e se eu vender mais X% /
 * cortar custo em Y%"). Recebe as métricas atuais + os percentuais simulados.
 */
export function calculateProjection(rev, cos, ebitda, simRevenuePct, simCostsPct) {
    const sRev = Number(simRevenuePct) || 0;
    const sCos = Number(simCostsPct) || 0;
    const projectedRev = rev * (1 + (sRev / 100));
    const projectedCos = cos * (1 - (sCos / 100));
    const projectedEbitda = projectedRev - projectedCos;
    const gains = projectedEbitda - ebitda;
    const projectedMargin = projectedRev > 0
        ? Number(((projectedEbitda / projectedRev) * 100).toFixed(1))
        : 0;

    return { projectedRev, projectedCos, projectedEbitda, gains, projectedMargin };
}